/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useEffect, useState } from "react";
import { useTranslation } from "@i18n";
import { type TranslatedString, untranslated } from "@Helpers/types/i18n";
import { defaultModulesMapper } from "@Helpers/constants";
import { TOAST_TYPE_WARNING } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type { SpacesDataResponse } from "@ServerTypes/spaces/space.schema";

export interface Space {
  _id: string;
  id?: string;
  spaceName: string;
  isDefault?: boolean;
  modulePermissions: any;
}

interface UseSelectSpacesProps {
  projectId: string;
  workspaceId?: string;
  selectedSpaceCallback: (space: Space | null, spaceData?: Space[] | null) => void;
  fetchSpaceListCallback?: (response: any) => void;
  dataSource?: Space[] | null;
  disablePermissionCheck?: boolean;
  moduleName?: keyof typeof defaultModulesMapper;
}

function useSelectSpaces({
  projectId,
  workspaceId,
  selectedSpaceCallback,
  fetchSpaceListCallback,
  dataSource,
  disablePermissionCheck,
  moduleName = "",
}: UseSelectSpacesProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [spaceData, setSpaceData] = useState<Space[] | null>(dataSource ?? null);
  const [selectedSpace, setSelectedSpace] = useState<Space | null>(null);
  const [cannotCreateInAnySpace, setCannotCreateInAnySpace] = useState<boolean>(false);
  const [switchSpaceId, setSwitchSpaceId] = useState<string | undefined>(workspaceId);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (workspaceId) {
      setSwitchSpaceId(workspaceId);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchSpaceList();
  }, [projectId, dataSource]);

  useEffect(() => {
    if (cannotCreateInAnySpace && spaceData?.length) {
      dispatch(
        showToastNotification({
          content: t("You do not have create permission for this entity in any space within this project."),
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
  }, [cannotCreateInAnySpace, spaceData]);

  useEffect(() => {
    if (!selectedSpace && spaceData && switchSpaceId) {
      const space = spaceData.find((item) => item._id === switchSpaceId || item.id === switchSpaceId) ?? null;
      if (space) {
        setSelectedSpace(space);
        selectedSpaceCallback(space, spaceData);
      }
    }
  }, [spaceData, switchSpaceId]);

  const spaceSelectCheck = (item: any) => {
    return disablePermissionCheck || item?.modulePermissions?.[moduleName]?.canCreate;
  };

  async function fetchSpaceList() {
    if (dataSource) {
      setSpaceData(dataSource);
      if (!switchSpaceId && Array.isArray(dataSource) && dataSource.length > 0) {
        const defaultSpace = dataSource.find((item) => item.isDefault);
        const firstSpace = dataSource[0];
        const initialSpaceId = defaultSpace?._id ?? firstSpace?.id;
        setSwitchSpaceId(initialSpaceId);
      }
    } else {
      setIsLoading(true);
      try {
        const response = await axiosWrapper.post<SpacesDataResponse>(`${getBackendUrl()}/spaces/spacesData`, {
          value: {
            projectId,
          },
        });
        fetchSpaceListCallback?.(response);
        if (!switchSpaceId) {
          const resultArr = response.data?.result as any[] | undefined;
          const defaultSpace = resultArr?.find((item: any) => item.isDefault && spaceSelectCheck(item));

          const initialSpaceId = defaultSpace?._id ?? resultArr?.find((item: any) => spaceSelectCheck(item))?._id;

          if (initialSpaceId) {
            setSwitchSpaceId(initialSpaceId);
          } else {
            setCannotCreateInAnySpace(true);
          }
        }
        if (response.data.result) {
          setSpaceData(response.data.result as Space[]);
        } else {
          setSpaceData([]);
        }
      } catch (error) {
        console.error("Error fetching spaces:", error);
        setSpaceData([]);
        Promise.reject(error);
      } finally {
        setIsLoading(false);
      }
    }
  }

  const selectSpace = (space: Space) => {
    if (spaceSelectCheck(space)) {
      setSwitchSpaceId(space._id);
      selectedSpaceCallback(space, spaceData);
      setSelectedSpace(space);
    } else {
      dispatch(
        showToastNotification({
          content: t("You do not have create permission for this entity in this space"),
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
  };

  const openSelector = () => {
    setIsOpen(true);
  };

  const closeSelector = () => {
    setIsOpen(false);
  };

  const getSpaceDisplayText = (): TranslatedString => {
    if (!selectedSpace) return untranslated("");
    return selectedSpace.isDefault
      ? untranslated(`${selectedSpace.spaceName} (${t("Default")})`)
      : untranslated(selectedSpace.spaceName);
  };

  const isSpaceSelected = (space: Space) => {
    const spaceId = space._id || space.id;
    return spaceId === switchSpaceId;
  };

  const hasSpaces = spaceData !== null && spaceData.length > 0;

  const noSpacesAvailable = spaceData !== null && spaceData.length === 0;

  return {
    // State
    spaceData,
    selectedSpace,
    switchSpaceId,
    isLoading,
    isOpen,
    cannotCreateInAnySpace,

    // Actions
    selectSpace,
    openSelector,
    closeSelector,
    fetchSpaceList,

    // Computed
    getSpaceDisplayText,
    isSpaceSelected,
    hasSpaces,
    noSpacesAvailable,
  };
}

export default useSelectSpaces;
